import React, { useState, useEffect, useRef, useMemo } from "react";
import { ReactComponent as MobileLogo } from "../../assets/images/svg/mobile-logo.svg";
import { ReactComponent as MobileSponsorIcon } from "../../assets/images/svg/mobile-sponser.svg";
import { ReactComponent as PlayIcon } from "../../assets/images/svg/play.svg";
import { ReactComponent as Likes } from "../../assets/images/svg/likes-button.svg";
import { ReactComponent as CommentSection } from "../../assets/images/svg/comment-section.svg";
import { ReactComponent as FacebookIcon } from "../../assets/images/svg/facebook-icon.svg";
import { ReactComponent as InstagramIcon } from "../../assets/images/svg/instagram-icon.svg";
import { ReactComponent as EnableFb } from "../../assets/images/svg/enable-fb.svg";
import { ReactComponent as DisableFb } from "../../assets/images/svg/disable-fb.svg";
import { ReactComponent as EnableInsta } from "../../assets/images/svg/enable-insta.svg";
import { ReactComponent as DisableInsta } from "../../assets/images/svg/disab-insta.svg";
import { ReactComponent as InstagramLike } from "../../assets/images/svg/insta-like.svg";
import { ReactComponent as InstagramComment } from "../../assets/images/svg/insta-comment.svg";
import { ReactComponent as SpotifyAdView } from "../../assets/images/svg/spotify-mobile-preview.svg";
import { ReactComponent as SpotifyIcon } from "../../assets/images/svg/spotify-icon.svg";
import switcherBg from "../../assets/images/svg/mab-Switcher.svg";
import screenBg from "../../assets/images/svg/Screen.svg";
import VideoSelectorDropdown from "./meta-video-selector-dropdown";

type VideoItem = {
  id: string;
  fileName: string;
  file?: File;
  isUploading: boolean;
  uploadProgress: number;
  facebookVideoId?: string;
  videoUrl?: string;
  thumbnailUrl?: string;
};

type MobileAdPreviewProps = {
  showPreview: boolean;
  videoFile?: File | null;
  videos?: VideoItem[];
  selectedVideoId?: string | null;
  onVideoSelect?: (videoId: string) => void;
  audioFile?: File | null;
  trackImageUrl?: string | null;
  landingImageFile?: File | null;
  title?: string;
  artistName?: string;
  adText?: string;
  adHeadline?: string;
  spotifyUrl?: string;
  // Meta Ads account names (for edit/draft mode)
  instagramName?: string | null;
  pageName?: string | null;
};

const MobileAdPreview = ({
  showPreview,
  videoFile,
  videos = [],
  selectedVideoId = null,
  onVideoSelect = () => { },
  audioFile,
  trackImageUrl,
  landingImageFile,
  title,
  artistName,
  adText,
  adHeadline,
  spotifyUrl,
  instagramName,
  pageName
}: MobileAdPreviewProps) => {
  const [media, setMedia] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<"image" | "video" | null>(null);
  const [thumbnailImage, setThumbnailImage] = useState<string | null>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [isSeeking, setIsSeeking] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<"facebook" | "instagram">("facebook");
  const [selectedScreen, setSelectedScreen] = useState<"ad" | "afterClick">("ad");
  const [isAdTextExpanded, setIsAdTextExpanded] = useState(false);
  const previousBlobUrlRef = useRef<string | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const landingImageUrlRef = useRef<string | null>(null);

  // Determine the current video file based on selection
  const currentVideoFile = useMemo(() => {
    if (!videos || videos.length === 0) {
      return videoFile; // Fallback to old prop for backward compatibility
    }

    const selectedVideo = videos.find(video => video.id === selectedVideoId);
    if (selectedVideo) {
      return selectedVideo.file;
    }

    // If no selected video, use the first completed video
    const firstCompletedVideo = videos.find(video => !video.isUploading);
    return firstCompletedVideo?.file || videoFile;
  }, [videos, selectedVideoId, videoFile]);

  // Determine the current video URL (for Facebook videos)
  const currentVideoUrl = useMemo(() => {
    if (!videos || videos.length === 0) {
      return null;
    }

    const selectedVideo = videos.find(video => video.id === selectedVideoId);
    if (selectedVideo) {
      // Only use videoUrl if it's a Facebook URL (contains facebook.com or fbcdn.net)
      // For our streaming URLs, we'll handle them differently
      const url = selectedVideo.videoUrl || null;
      if (url && (url.includes('facebook.com') || url.includes('fbcdn.net') || url.includes('fb.com'))) {
        return url;
      }
      return null; // Don't use streaming URL directly, will fetch as blob
    }

    // If no selected video, use the first completed video
    const firstCompletedVideo = videos.find(video => !video.isUploading);
    const url = firstCompletedVideo?.videoUrl || null;
    if (url && (url.includes('facebook.com') || url.includes('fbcdn.net') || url.includes('fb.com'))) {
      return url;
    }
    return null;
  }, [videos, selectedVideoId]);

  // Get meta ads account data from sessionStorage (same as used in meta-new-compaing.tsx)
  const metaAdsAccountData = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = sessionStorage.getItem("metaAdsSelections");
      if (!raw) return null;

      const parsed = JSON.parse(raw);
      // The sessionStorage structure now includes images for page and instagram
      return {
        page: parsed?.page ? {
          name: parsed.page.name,
          image: parsed.page.image || null
        } : null,
        instagram: parsed?.instagram ? {
          name: parsed.instagram.name,
          image: parsed.instagram.image || null
        } : null,
        adAccount: parsed?.adAccount ? {
          name: parsed.adAccount.name,
          id: parsed.adAccount.id
        } : null,
      };
    } catch (e) {
      console.error("Error parsing metaAdsSelections:", e);
      return null;
    }
  }, []);

  // Get account image and name based on selected platform
  const accountImage = useMemo(() => {
    // If Instagram is selected, use Instagram account image
    if (selectedPlatform === "instagram") {
      if (metaAdsAccountData?.instagram?.image) {
        return metaAdsAccountData.instagram.image;
      }
    }
    // If Facebook is selected, use Facebook page image
    if (selectedPlatform === "facebook") {
      if (metaAdsAccountData?.page?.image) {
        return metaAdsAccountData.page.image;
      }
    }
    // Fallback to user profile image
  }, [selectedPlatform, metaAdsAccountData]);

  const accountName = useMemo(() => {
    // If Instagram is selected, use Instagram account name
    if (selectedPlatform === "instagram") {
      // Priority: Props > SessionStorage > Username > Default
      if (instagramName) return instagramName;
      if (metaAdsAccountData?.instagram?.name) {
        return metaAdsAccountData.instagram.name;
      }
    }
    // If Facebook is selected, use Facebook page name
    if (selectedPlatform === "facebook") {
      // Priority: Props > SessionStorage > Username > Default
      if (pageName) return pageName;
      if (metaAdsAccountData?.page?.name) {
        return metaAdsAccountData.page.name;
      }
    }
  }, [selectedPlatform, metaAdsAccountData, instagramName, pageName]);

  useEffect(() => {
    const loadVideo = async () => {
      if (previousBlobUrlRef.current) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
        previousBlobUrlRef.current = null;
      }
      if (landingImageUrlRef.current) {
        URL.revokeObjectURL(landingImageUrlRef.current);
        landingImageUrlRef.current = null;
      }

      // Setup landing image as thumbnail
      if (landingImageFile) {
        const url = URL.createObjectURL(landingImageFile);
        landingImageUrlRef.current = url;
        setThumbnailImage(url);
      } else {
        setThumbnailImage(null);
      }

      // Setup video URL - handle File, Facebook URL, or streaming URL
      if (currentVideoFile) {
        // Local video file
        const url = URL.createObjectURL(currentVideoFile);
        previousBlobUrlRef.current = url;
        setVideoUrl(url);
        finalizeMediaSetup(url);
      } else if (currentVideoUrl) {
        // Facebook video URL (public, no auth needed)
        setVideoUrl(currentVideoUrl);
        finalizeMediaSetup(currentVideoUrl);
      } else {
        // Check if there's a streaming URL from S3 (our draft videos)
        const selectedVideo = videos?.find(v => v.id === selectedVideoId);
        const firstCompletedVideo = videos?.find(v => !v.isUploading);
        const videoToLoad = selectedVideo || firstCompletedVideo;

        if (videoToLoad?.videoUrl && !videoToLoad.file) {
          // This is a streaming URL from S3, fetch it as blob
          try {
            const token = localStorage.getItem("metaAdsPublicToken");
            const response = await fetch(videoToLoad.videoUrl, {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            });

            if (!response.ok) {
              throw new Error(`Failed to fetch video: ${response.statusText}`);
            }

            const blob = await response.blob();
            const url = URL.createObjectURL(blob);
            previousBlobUrlRef.current = url;
            setVideoUrl(url);
            finalizeMediaSetup(url);
          } catch (error) {
            console.error('Error loading streaming video:', error);
            setVideoUrl(null);
            setMedia(null);
            setMediaType(null);
            setIsPlaying(false);
          }
        } else {
          setVideoUrl(null);
          setMedia(null);
          setMediaType(null);
          setIsPlaying(false);
        }
      }
    };

    const finalizeMediaSetup = (videoUrlForMedia: string | null) => {
      // Determine what to show initially
      // Landing page takes priority - always show it if available
      if (landingImageFile) {
        setMedia(landingImageUrlRef.current);
        setMediaType("image");
        setIsPlaying(false);
      } else if (videoUrlForMedia) {
        // Video (local, Facebook, or streamed)
        setMedia(videoUrlForMedia);
        setMediaType("video");
        setIsPlaying(false);
      } else {
        // No media
        setMedia(null);
        setMediaType(null);
        setIsPlaying(false);
      }
    };

    loadVideo();

    return () => {
      if (previousBlobUrlRef.current) {
        URL.revokeObjectURL(previousBlobUrlRef.current);
        previousBlobUrlRef.current = null;
      }
      if (landingImageUrlRef.current) {
        URL.revokeObjectURL(landingImageUrlRef.current);
        landingImageUrlRef.current = null;
      }
    };
  }, [currentVideoFile, currentVideoUrl, landingImageFile, videos, selectedVideoId]);

  const handlePlayClick = () => {
    // If both thumbnail and video exist, switch to video
    if (thumbnailImage && videoUrl && mediaType === "image") {
      setMedia(videoUrl);
      setMediaType("video");
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.play();
          setIsPlaying(true);
        }
      }, 100);
    } else if (videoRef.current) {
      videoRef.current.play();
      setIsPlaying(true);
    }
  };

  const handleVideoPause = () => {
    setIsPlaying(false);
    // Don't switch to thumbnail - keep video frame visible when paused
  };

  const handleVideoPlay = () => {
    setIsPlaying(true);
  };

  const handleVideoEnded = () => {
    setIsPlaying(false);
    // Switch back to thumbnail when video ends
    if (thumbnailImage && videoUrl) {
      setMedia(thumbnailImage);
      setMediaType("image");
    }
  };

  // Video time tracking handlers
  const handleTimeUpdate = () => {
    if (videoRef.current && !isSeeking) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
    }
  };

  const handleProgress = () => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1);
      setBuffered(bufferedEnd);
    }
  };

  const handleVideoToggle = () => {
    if (!videoRef.current) return;

    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play();
    }
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    if (!videoRef.current) return;

    const timeline = e.currentTarget;
    const rect = timeline.getBoundingClientRect();
    const pos = (e.clientX - rect.left) / rect.width;
    const seekTime = pos * duration;

    videoRef.current.currentTime = seekTime;
    setCurrentTime(seekTime);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // Audio playback handlers
  const handleAudioPlay = () => {
    if (audioRef.current) {
      if (isAudioPlaying) {
        audioRef.current.pause();
        setIsAudioPlaying(false);
      } else {
        audioRef.current.play();
        setIsAudioPlaying(true);
      }
    }
  };

  const handleAudioEnded = () => {
    setIsAudioPlaying(false);
  };

  // Setup audio element URL
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  useEffect(() => {
    if (audioFile) {
      const url = URL.createObjectURL(audioFile);
      setAudioUrl(url);

      return () => {
        URL.revokeObjectURL(url);
      };
    } else {
      setAudioUrl(null);
    }
  }, [audioFile]);

  // Pause audio when leaving "afterClick" screen
  useEffect(() => {
    if (selectedScreen !== "afterClick" && isAudioPlaying) {
      if (audioRef.current) {
        audioRef.current.pause();
      }
      setIsAudioPlaying(false);
    }
  }, [selectedScreen, isAudioPlaying]);

  // Show landing image when switching to "afterClick" screen
  useEffect(() => {
    if (selectedScreen === "afterClick" && landingImageFile) {
      setMedia(landingImageUrlRef.current);
      setMediaType("image");
      setIsPlaying(false);
    }
  }, [selectedScreen, landingImageFile]);

  if (!showPreview) return null;

  return (
    <>
      {/* Hidden audio element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onEnded={handleAudioEnded}
          style={{ display: "none" }}
        />
      )}
      <div className="flex flex-col items-center mx-auto" style={{ width: "100%", maxWidth: "450px", minWidth: "350px", transform: "scale(0.8)", transformOrigin: "center top" }}>
        <div className="flex flex-col items-center mt-4 bg-[#1A1A1A] pt-[30px] pb-[20px] px-8 sm:px-12 md:px-16 rounded-lg w-full" style={{ borderRadius: "20px" }}>
          {/* Video Selector and Social Media Buttons - Only show on "Screen of ad" */}
          {selectedScreen === "ad" && (
            <div className="flex items-center gap-[120px] mb-4">
              {/* Video Selector Dropdown */}
              {videos && videos.length > 0 && onVideoSelect && (
                <VideoSelectorDropdown
                  videos={videos}
                  selectedVideoId={selectedVideoId}
                  onVideoSelect={onVideoSelect}
                  className="w-[180px]"
                />
              )}

              {/* Facebook/Instagram Buttons */}
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSelectedPlatform("facebook")}
                  className="flex items-center justify-center"
                >
                  {selectedPlatform === "facebook" ? (
                    <EnableFb className="w-auto h-auto" />
                  ) : (
                    <DisableFb className="w-auto h-auto" />
                  )}
                </button>
                {/* Vertical separator line */}
                <div className="h-6 w-px bg-[#878DA1] opacity-50" />
                <button
                  onClick={() => setSelectedPlatform("instagram")}
                  className="flex items-center justify-center"
                >
                  {selectedPlatform === "instagram" ? (
                    <EnableInsta className="w-auto h-auto" />
                  ) : (
                    <DisableInsta className="w-auto h-auto" />
                  )}
                </button>
              </div>
            </div>
          )}

          <div
            className="relative mx-auto"
            style={{
              width: "330px",
              height: "670px",
              borderRadius: "45px",
              padding: "10px",
              background: "#0f0f0f",
              boxShadow: `
      0 0 0 1px rgba(244,245,240,0.85),
      inset 0 0 0 1px rgba(175,176,171,0.45),
      0 0 50px rgba(0,0,0,0.45)
    `,
            }}
          >


            {/* SCREEN */}
            <div
              className={`relative overflow-y-auto ${selectedScreen === "afterClick" ? "bg-black" : "bg-[#f2f2f2]"}`}
              style={{
                width: "100%",
                height: "100%",
                borderRadius: "35px",
                ...(selectedScreen === "afterClick" && {
                  backgroundImage: `url(${screenBg})`,
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                  backgroundRepeat: "no-repeat",
                }),
              }}
            >

              {/* GRADIENT OVERLAY - Behind notch for afterClick */}
              {selectedScreen === "afterClick" && (
                <div
                  className="absolute top-0 left-0 right-0"
                  style={{
                    height: "80px",
                    background: "linear-gradient(to bottom, rgba(30, 70, 80, 0.95) 0%, rgba(25, 60, 70, 0.85) 20%, rgba(20, 50, 60, 0.7) 40%, rgba(15, 40, 50, 0.5) 60%, rgba(10, 30, 40, 0.3) 80%, transparent 100%)",
                    pointerEvents: "none",
                    zIndex: 1,
                  }}
                />
              )}

              {/* 🔵 Dynamic Island */}
              <div
                className="absolute left-1/2 -translate-x-1/2"
                style={{
                  top: "10px",
                  width: "80px",
                  height: "20px",
                  backgroundColor: "black",
                  borderRadius: "20px",
                  zIndex: 2,
                }}
              />

              {/* TOP GRAY SPACING with gradient for afterClick */}
              <div
                className={`w-full h-10 mt-10 ${selectedScreen === "afterClick" ? "" : "bg-[#f2f2f2]"}`}
                style={selectedScreen === "afterClick" ? {
                  background: "linear-gradient(to bottom, rgba(25, 60, 70, 0.8) 0%, rgba(20, 50, 60, 0.6) 50%, rgba(15, 40, 50, 0.4) 100%)",
                } : {}}
              />

              {/* MAIN FB CARD */}
              {selectedScreen === "afterClick" ? (
                <>
                  {/* SPOTIFY VIEW - After Click */}
                  <div className="w-[260px] mx-auto bg-black mb-8 relative" style={{ marginTop: "-15px" }}>
                    {/* MEDIA AREA - 1:1 aspect ratio */}
                    <div
                      className="relative w-full bg-black overflow-hidden"
                      style={{ aspectRatio: "1/1" }}
                      onClick={audioFile ? handleAudioPlay : undefined}
                    >
                      {mediaType === "image" && media && (
                        <img src={media} className="w-full h-full object-cover" />
                      )}

                      {mediaType === "video" && media && (
                        <img src={media} className="w-full h-full object-cover" />
                      )}

                      {/* Show placeholder only if no media */}
                      {!media && (
                        <div className="w-full h-full flex items-center justify-center bg-[#1F2733]">
                          <span className="text-white text-sm opacity-50">No media uploaded</span>
                        </div>
                      )}

                      {/* WHITE PLAY/PAUSE ICON - For audio control */}
                      {audioFile && media && (
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-[120px] h-[120px] border-white flex items-center justify-center">
                            {isAudioPlaying ? (
                              // Pause icon (two vertical bars)
                              <svg className="w-[58px] h-[58px] text-white" fill="currentColor" viewBox="0 0 24 24">
                                <rect x="6" y="4" width="4" height="16" rx="1" />
                                <rect x="14" y="4" width="4" height="16" rx="1" />
                              </svg>
                            ) : (
                              <PlayIcon className="w-[58px] h-[58px] text-white" />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>


                  <div className="flex flex-col justify-center items-center mt-[-20px] ">

                    {/* SONG INFORMATION */}
                    <div className="w-full bg-black px-4 py-4 text-center">
                      <h4 className="text-white font-semibold text-lg mb-1">
                        {title || "Chains of Love"}
                      </h4>
                      <p className="text-white text-sm">
                        {artistName || "Charli xcx"}
                      </p>
                    </div>

                    {/* SPOTIFY AD VIEW - Using SVG */}
                    <div className="w-full bg-black mt-[-10px] ">
                      <SpotifyAdView width="" height="149" />
                    </div>
                  </div>
                </>
              ) : (
                /* REGULAR AD VIEW */
                <div className={`w-[300px] mx-auto bg-white border border-gray-300 ${selectedPlatform === "instagram" ? "mb-2" : "mb-8"}`} style={{ marginTop: "-15px" }}>
                  {/* HEADER */}
                  <div className="flex justify-between px-3 pt-3 pb-2 bg-white">
                    <div className="flex gap-2">
                      {/* Account Image or Logo */}
                      <div className="w-10 h-10 rounded-full bg-black flex items-center justify-center" style={{ overflow: "hidden" }}>
                        {accountImage ? (
                          <img
                            src={accountImage}
                            alt="Profile"
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <MobileLogo width="24" height="24" style={{ display: "block", width: "24px", height: "24px" }} />
                        )}
                      </div>

                      <div className="flex flex-col leading-tight">
                        <span className="font-semibold text-[14px]">{accountName}</span>
                        <div className="flex items-center gap-1 text-gray-500 text-[12px]">
                          <span>Sponsored</span>
                          <span className="text-[20px] leading-none">·</span>
                          <MobileSponsorIcon className="w-[14px] h-[14px]" />
                        </div>
                      </div>
                    </div>

                    <div className="text-[24px] leading-none">
                      {selectedPlatform === "instagram" ? "−" : "⋯"}
                    </div>
                  </div>

                  {/* MAIN TEXT */}
                  {
                    selectedPlatform === "facebook" && adText && adText.trim() !== "" && (
                      <div className="px-3 pb-2">
                        {!isAdTextExpanded ? (
                          <>
                            <p className="text-[14px] break-words line-clamp-2">
                              {adText}
                            </p>
                            {adText.length > 80 && (
                              <button
                                onClick={() => setIsAdTextExpanded(true)}
                                className="text-[14px] text-gray-500 hover:underline mt-1"
                              >
                                See more
                              </button>
                            )}
                          </>
                        ) : (
                          <>
                            <p className="text-[14px] break-words">
                              {adText}
                            </p>
                            <button
                              onClick={() => setIsAdTextExpanded(false)}
                              className="text-[14px] text-gray-500 hover:underline mt-1"
                            >
                              See less
                            </button>
                          </>
                        )}
                      </div>
                    )
                  }

                  {/* MEDIA - 1:1 aspect ratio */}
                  <div
                    className="relative w-full bg-black overflow-hidden cursor-pointer"
                    style={{ aspectRatio: "1/1" }}
                    onClick={mediaType === "video" ? handleVideoToggle : undefined}
                  >
                    {mediaType === "image" && media && (
                      <img src={media} className="w-full h-full object-cover" />
                    )}

                    {mediaType === "video" && media && (
                      <>
                        <video
                          ref={videoRef}
                          src={media}
                          className="w-full h-full object-cover"
                          controls={false}
                          onPlay={handleVideoPlay}
                          onPause={handleVideoPause}
                          onEnded={handleVideoEnded}
                          onTimeUpdate={handleTimeUpdate}
                          onLoadedMetadata={handleLoadedMetadata}
                          onProgress={handleProgress}
                        />

                        {/* Custom Video Controls */}
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent px-3 py-2 pb-6">
                          <div className="flex items-center gap-2">
                            {/* Play/Pause Button */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                if (videoRef.current) {
                                  if (isPlaying) {
                                    videoRef.current.pause();
                                  } else {
                                    videoRef.current.play();
                                  }
                                }
                              }}
                              className="w-8 h-8 flex items-center justify-center text-white hover:scale-110 transition-transform"
                            >
                              {isPlaying ? (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                  <rect x="0" y="0" width="4" height="12" />
                                  <rect x="6" y="0" width="4" height="12" />
                                </svg>
                              ) : (
                                <svg width="12" height="12" viewBox="0 0 12 12" fill="currentColor">
                                  <path d="M0 0L10 6L0 12V0Z" />
                                </svg>
                              )}
                            </button>

                            {/* Timeline */}
                            <div
                              className="flex-1 h-1 bg-white/30 rounded-full cursor-pointer relative"
                              onClick={handleSeek}
                            >
                              {/* Buffered portion */}
                              <div
                                className="absolute h-full bg-white/50 rounded-full"
                                style={{ width: `${(buffered / duration) * 100}%` }}
                              />
                              {/* Played portion */}
                              <div
                                className="absolute h-full bg-[#58F2A9] rounded-full"
                                style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                              />
                            </div>

                            {/* Time Display */}
                            <span className="text-white text-[10px] font-medium whitespace-nowrap" style={{ fontFamily: 'Poppins, sans-serif' }}>
                              {formatTime(currentTime)} / {formatTime(duration)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}

                    {/* Show placeholder only if no media */}
                    {!media && (
                      <div className="w-full h-full flex items-center justify-center bg-[#1F2733]">
                        <span className="text-white text-sm opacity-50">No media uploaded</span>
                      </div>
                    )}

                    {/* WHITE PLAY ICON - Show for images or videos that are not playing */}
                    {(!isPlaying && mediaType === "video" && media) && (
                      <div
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={handlePlayClick}
                      >
                        <div className="w-[120px] h-[120px] border-white flex items-center justify-center">
                          <PlayIcon className="w-[70px] h-[70px] text-white" />
                        </div>
                      </div>
                    )}

                    {mediaType === "image" && media && videoUrl && (
                      <div
                        className="absolute inset-0 flex items-center justify-center cursor-pointer"
                        onClick={handlePlayClick}
                      >
                        <div className="w-[120px] h-[120px] border-white flex items-center justify-center">
                          <PlayIcon className="w-[58px] h-[58px] text-white" />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* BOTTOM SECTION - Only show for Facebook */}
                  {selectedPlatform === "facebook" && (
                    <div className="flex justify-between items-center gap-2 px-4 py-3 border-t bg-white">
                      <div className="flex-1 min-w-0">
                        <p className="text-gray-500 text-xs">pitchplaylists.com</p>
                        <p className="font-semibold text-sm break-words line-clamp-2">{adHeadline || "Listen now on Spotify!"}</p>
                      </div>

                      <button
                        //  onClick={() => setSelectedScreen("afterClick")}
                        className="bg-gray-200 px-4 py-2 rounded-lg text-xs font-semibold  transition-opacity cursor-pointer flex-shrink-0"
                      >
                        Listen Now
                      </button>
                    </div>
                  )}

                  {/* REACTIONS */}
                  {selectedPlatform === "facebook" ? (
                    <div className="px-4 py-2 flex justify-between border-t bg-white">
                      <div className="flex items-center gap-1">
                        <Likes className="w-[60px] h-full" />
                      </div>
                      <div className="flex gap-1 text-[12px] text-gray-600">
                        <span>12 Comments</span>
                        <span>8 Shares</span>
                      </div>
                    </div>
                  ) : (
                    <div
                      //  onClick={() => setSelectedScreen("afterClick")}
                      className="border-t w-full flex items-center justify-start cursor-pointer  transition-opacity"
                    >
                      <InstagramLike className="w-[300px] h-[40px]" />

                    </div>
                  )}

                  {
                    selectedPlatform === "instagram" && (
                      <div className="flex justify-around items-center py-1 text-gray-700 text-[12px] font-medium">
                        <InstagramComment height="45" />
                      </div>
                    )
                  }
                  {selectedPlatform === "instagram" && adText && adText.trim() !== "" && (
                    <div className="px-3 pb-1">
                      {!isAdTextExpanded ? (
                        <>
                          <p className="text-[14px] break-words line-clamp-2">
                            {adText}
                          </p>
                          {adText.length > 80 && (
                            <button
                              onClick={() => setIsAdTextExpanded(true)}
                              className="text-[14px] text-gray-500 hover:underline mt-1"
                            >
                              See more
                            </button>
                          )}
                        </>
                      ) : (
                        <>
                          <p className="text-[14px] break-words">
                            {adText}
                          </p>
                          <button
                            onClick={() => setIsAdTextExpanded(false)}
                            className="text-[14px] text-gray-500 hover:underline mt-1"
                          >
                            See less
                          </button>
                        </>
                      )}
                    </div>
                  )}

                  {/* LINE - Only show for Facebook */}
                  {selectedPlatform === "facebook" && (
                    <div className="border-b"></div>
                  )}


                  {
                    selectedPlatform === "facebook" && (
                      <div className="flex justify-around items-center py-2 text-gray-700 text-[12px] font-medium bg-white">
                        <CommentSection width="300" height="45" />
                      </div>
                    )}
                </div>
              )}



            </div>
          </div>
        </div>
        {/* <div className="mt-2 flex items-center justify-center bg-[#878DA11A] h-[95px] w-full" style={{ borderRadius: "20px" }}>
        <div
          className="relative flex items-center p-1 rounded-full"
          style={{
            width: "360px",
            height: "68px",                     // match screenshot
            background: selectedScreen === "ad"
              ? "linear-gradient(90deg, #58F2A9 0%, #1C3941 100%)"  // When "ad" is active: light green (left, active) to dark green (right, non-active)
              : "linear-gradient(270deg, #58F2A9 0%, #1C3941 100%)", // When "afterClick" is active: dark green (left, non-active) to light green (right, active)
            borderRadius: "999px",
            padding: "4px",
            transition: "background 0.3s ease",
          }}
        >
          <button
            onClick={() => setSelectedScreen("ad")}
            className="rounded-full transition-all duration-300 flex items-center justify-center"
            style={{
              width: "155px",
              height: "54px",
              background: selectedScreen === "ad" ? "#FFFFFF" : "transparent",
              color: selectedScreen === "ad" ? "#000000" : "#FFFFFF",
              borderRadius: "999px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontStyle: "normal",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "0%",
              padding: 0,
              marginLeft: 4,
            }}
          >
            Screen of ad
          </button>

          <button
            onClick={() => setSelectedScreen("afterClick")}
            className="rounded-full transition-all duration-300 flex items-center justify-center"
            style={{
              width: "190px",
              height: "54px",
              background: selectedScreen === "afterClick" ? "#FFFFFF" : "transparent",
              color: selectedScreen === "afterClick" ? "#000000" : "#FFFFFF",
              borderRadius: "999px",
              fontFamily: "Poppins, sans-serif",
              fontWeight: 600,
              fontStyle: "normal",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "0%",
              padding: 0,
              margin: 0,
            }}
          >
            Screen after ad click
          </button>
        </div>
      </div> */}
      </div>
    </>
  );
};

export default MobileAdPreview;
